import { PERFORMANCE_STATUS } from './constants';
import { PerformanceMonitoringData } from '../../../types';

const RANGE_MIN = -50;
const RANGE_MAX = 50;

export const fillMissingRangeDataWithZerosAndComputeTotals = (
  rawData?: PerformanceMonitoringData[]
): { data: PerformanceMonitoringData[]; totalCampaigns: number } => {
  if (!rawData?.length) {
    return { data: [], totalCampaigns: 0 };
  }

  // Clamp each entry's range to [RANGE_MIN, RANGE_MAX] and merge counts for
  // any entries that fall outside the bounds into the boundary buckets.
  const dataByRange = new Map<number, PerformanceMonitoringData>();
  for (const entry of rawData) {
    const clampedRange = Math.max(RANGE_MIN, Math.min(RANGE_MAX, entry.range));
    const existing = dataByRange.get(clampedRange);
    if (existing) {
      Object.values(PERFORMANCE_STATUS).forEach((status) => {
        existing[status] = (existing[status] || 0) + (entry[status] || 0);
      });
    } else {
      dataByRange.set(clampedRange, { ...entry, range: clampedRange });
    }
  }

  const filledData: PerformanceMonitoringData[] = [];
  let totalCampaigns = 0;

  for (let range = RANGE_MIN; range <= RANGE_MAX; range += 5) {
    const dataEntry: PerformanceMonitoringData = {
      range,
      [PERFORMANCE_STATUS.EFFICIENT_ON_TRACK]: 0,
      [PERFORMANCE_STATUS.EFFICIENT_AT_RISK]: 0,
      [PERFORMANCE_STATUS.INEFFICIENT_ON_TRACK]: 0,
      [PERFORMANCE_STATUS.INEFFICIENT_AT_RISK]: 0,
    };

    const incomingRangeData = dataByRange.get(range);
    // Add campaigns counts per status for the range present in the incoming data and calculate the total number of campaigns.
    // Otherwise, the range will be filled with 0 counts for all statuses, and totalCampaigns will remain unchanged.
    if (incomingRangeData) {
      Object.values(PERFORMANCE_STATUS).forEach((status) => {
        const earlierStatusCount = incomingRangeData[status] || 0;
        dataEntry[status] = earlierStatusCount;
        totalCampaigns += earlierStatusCount;
      });
    }

    filledData.push(dataEntry);
  }

  return { data: filledData, totalCampaigns };
};

export const formatRangeLabel = (range: number) => {
  if (range === 0) {
    return '0%';
  }
  if (range <= RANGE_MIN) {
    return `${RANGE_MIN.toString()}%+`;
  }
  if (range >= RANGE_MAX) {
    return `+${RANGE_MAX.toString()}%+`;
  }
  return range < 0 ? `${range.toString()}%` : `+${range.toString()}%`;
};
